import { ClassNames, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { FadeLoader, SyncLoader } from 'react-spinners'

import { isTruthy } from '@bemlo/utils'

import { Color } from '../constants'
import { StyleProps, createStyles } from '../utils/create-styles'

type LoaderDesign = 'fade' | 'sync'

type LoaderSize = 'xs' | 'small' | 'medium'

const LOADER_SIZE: Record<
  LoaderDesign,
  Record<LoaderSize, Record<string, number>>
> = {
  sync: {
    xs: { size: 4 },
    small: { size: 8 },
    medium: {}, // Default
  },
  fade: {
    // Sizing does not work for fade loader...
    xs: {},
    small: {},
    medium: {},
  },
}

type DelayedFadeInProps = {
  /**
   * Will fade in the loader after about 200ms
   */
  delayed?: boolean
}

export type LoaderProps = {
  className?: string
  color?: string
  design?: LoaderDesign
  loading?: boolean
  size?: 'xs' | 'small' | 'medium' // TODO: switch to T-shirt sizes
  styles?: StyleProps
} & DelayedFadeInProps

const delayedFadeInAnimation = keyframes`
  from {
    opacity: 0;
  }

  75% {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

const fadeInAnimation = ({ delayed }: DelayedFadeInProps) => {
  return delayed
    ? {
        animationDuration: '0.3s',
        animationName: delayedFadeInAnimation,
      }
    : {}
}

const StyledFadeLoader = styled(FadeLoader, {
  shouldForwardProp: (prop) => prop !== 'delayed',
})(fadeInAnimation)
const StyledSyncLoader = styled(SyncLoader, {
  shouldForwardProp: (prop) => prop !== 'delayed',
})(fadeInAnimation)

export const Loader = (props: LoaderProps) => {
  const color = props.color ?? Color.BLUE
  const design = props.design ?? 'sync'
  const loading = props.loading

  return (
    <ClassNames>
      {({ css }) => {
        const className = [
          props.className,
          props.styles && css(createStyles(props.styles)),
        ]
          .filter(isTruthy)
          .join(' ')

        switch (design) {
          case 'fade':
            return (
              <StyledFadeLoader
                className={className}
                color={color}
                loading={loading}
                delayed={props.delayed}
                {...LOADER_SIZE[design][props.size ?? 'medium']}
              />
            )
          case 'sync':
            return (
              <StyledSyncLoader
                className={className}
                color={color}
                loading={loading}
                delayed={props.delayed}
                {...LOADER_SIZE[design][props.size ?? 'medium']}
              />
            )
        }
      }}
    </ClassNames>
  )
}
